import React, { useEffect, useState } from 'react';
import firebase from 'firebase/app';
import { allProjects } from 'search/builtinSearch';

/**
 * Task #19: auto-suggest existing values while typing ("Acc" -> "Accenture Australia").
 *
 * Renders one hidden <datalist> per field, filled from the built-in search file the browser already
 * has, so suggestions cost no extra database reads. An input opts in with list="sg-client" etc.
 * Most-used values come first, so the established spelling wins over one-off typos.
 */

export const SUGGEST = {
  title: 'sg-title',
  jobNumber: 'sg-jobNumber',
  client: 'sg-client',
  agency: 'sg-agency',
  tags: 'sg-tags',
  people: 'sg-people',
  /** clients and agencies together, for choosing what a client login may see */
  org: 'sg-org',
};

type Lists = { [K in keyof typeof SUGGEST]: string[] };
const EMPTY: Lists = { title: [], jobNumber: [], client: [], agency: [], tags: [], people: [], org: [] };
const MAX = 3000;

const ranked = (counts: Map<string, { n: number; label: string }>) =>
  Array.from(counts.values())
    .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label))
    .slice(0, MAX)
    .map((v) => v.label);

export function buildSuggestions(projects: IProject[]): Lists {
  const maps: { [K in keyof Lists]: Map<string, { n: number; label: string }> } = {
    title: new Map(),
    jobNumber: new Map(),
    client: new Map(),
    agency: new Map(),
    tags: new Map(),
    people: new Map(),
    org: new Map(),
  };
  const add = (key: keyof Lists, value: unknown) => {
    if (typeof value !== 'string') return;
    const label = value.trim();
    if (!label) return;
    const norm = label.toLowerCase();
    const hit = maps[key].get(norm);
    if (hit) hit.n++;
    else maps[key].set(norm, { n: 1, label });
  };
  for (const p of projects) {
    add('title', p.title);
    add('jobNumber', p.jobNumber);
    add('client', p.client);
    add('agency', p.agency);
    add('org', p.client);
    add('org', p.agency);
    if (Array.isArray(p.tags)) p.tags.forEach((t) => add('tags', t));
    [p.customField1, p.customField2, p.customField3].forEach((f) => f && add('people', f.value));
  }
  return {
    title: ranked(maps.title),
    jobNumber: ranked(maps.jobNumber),
    client: ranked(maps.client),
    agency: ranked(maps.agency),
    tags: ranked(maps.tags),
    people: ranked(maps.people),
    org: ranked(maps.org),
  };
}

const FieldSuggestions: React.FC = () => {
  const [lists, setLists] = useState<Lists>(EMPTY);

  useEffect(() => {
    let alive = true;
    let timer: number | undefined;
    const load = () =>
      allProjects()
        .then((projects) => alive && setLists(buildSuggestions(projects)))
        .catch(() => undefined); // not signed in yet, or no search file: no suggestions, nothing breaks
    const stop = firebase.auth().onAuthStateChanged((user) => {
      if (timer) window.clearInterval(timer);
      if (!user) return setLists(EMPTY);
      load();
      timer = window.setInterval(load, 5 * 60 * 1000); // pick up new clients and titles
    });
    return () => {
      alive = false;
      stop();
      if (timer) window.clearInterval(timer);
    };
  }, []);

  return (
    <>
      {(Object.keys(SUGGEST) as (keyof Lists)[]).map((key) => (
        <datalist key={key} id={SUGGEST[key]}>
          {lists[key].map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      ))}
    </>
  );
};

export default FieldSuggestions;
